import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword, onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import WelcomeLoader from "../pages/WelcomeLoader";

import bgImage from "../pages/images/2.jpeg";
import "./styles/adminlogin.css";

const AdminLogin = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) setShowWelcome(true);
    });
    return unsub;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (err) {
      setError(friendlyError(err.code));
      setLoading(false);
    }
  };

  if (showWelcome) {
    return (
      <WelcomeLoader
        name="Jabu Zwane"
        onDone={() => navigate("/admin/dashboard", { replace: true })}
      />
    );
  }

  return (
    <div className="al-root">
      <div className="al-card">

        {/* ── LEFT PANEL ── */}
        <div
          className="al-panel al-panel--left"
          style={{
            backgroundImage: `url(${bgImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="al-panel-overlay" />

          <div className="al-panel-content">
            <img src="/lg.png" alt="Jabuzwane" className="al-brand-logo" />

            <p className="al-panel-eyebrow">Nice to see you again</p>
            <h2 className="al-panel-headline">Welcome Back</h2>

            <div className="al-panel-divider" />

            <p className="al-panel-sub">
              Your space to manage sessions, books, and content.
            </p>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="al-panel al-panel--right">
          <div className="al-form-wrap">
            <p className="al-form-eyebrow">Admin Portal</p>
            <h1 className="al-form-heading">Sign in</h1>
            <p className="al-form-sub">
              Enter your credentials to access the dashboard.
            </p>

            <form className="al-form" onSubmit={handleSubmit} noValidate>
              <div className="al-field">
                <label htmlFor="al-email">Email</label>
                <div className="al-input-wrap">
                  <input
                    id="al-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div className="al-field">
                <label htmlFor="al-password">Password</label>
                <div className="al-input-wrap">
                  <input
                    id="al-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    placeholder="••••••••"
                    style={{ paddingRight: "40px" }}
                  />
                  <button
                    type="button"
                    className="al-toggle-visibility"
                    onClick={() => setShowPassword((v) => !v)}
                    disabled={loading}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="al-error" role="alert">
                  <WarnIcon />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                className="al-btn"
                disabled={loading || !email || !password}
              >
                <span className="al-btn-content">
                  {loading && <span className="al-spinner" />}
                  {loading ? "Signing in…" : "Sign in"}
                </span>
              </button>
            </form>
          </div>
        </div>

      </div>
    </div>
  );
};

/* ── Small inline icons (no extra deps) ── */
const EyeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a20.3 20.3 0 0 1 5.06-5.94M9.9 4.24A9.13 9.13 0 0 1 12 4c7 0 11 7 11 7a20.3 20.3 0 0 1-2.36 3.4" />
    <path d="M9.5 9.5a3 3 0 0 0 4.24 4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const WarnIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

function friendlyError(code) {
  switch (code) {
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Incorrect email or password.";
    case "auth/too-many-requests":
      return "Too many attempts. Try again in a few minutes.";
    case "auth/invalid-email":
      return "Enter a valid email address.";
    default:
      return "Sign-in failed. Please try again.";
  }
}

export default AdminLogin;